import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Disclaimer } from "@/components/site/Disclaimer";
import { toast } from "sonner";
import { CheckCircle2, Plus, X } from "lucide-react";
import {
  SERVICE_REQUIREMENTS,
  SHARED_FIELDS,
  RENDER_GROUPS,
  applyConditionalRules,
  computeRequiredFields,
  isEmptyValue,
  type FieldDef,
  type UIFlags,
} from "@/lib/intake-requirements";
import { trackIntakeOpened, trackIntakeSubmitted } from "@/lib/analytics";
import {
  LEGAL_VERSIONS,
  ServiceAgreementBody,
  LimitedAuthorizationBody,
  ElectronicSignatureConsentBody,
  PrivacyDataConsentBody,
  InformationCertificationBody,
} from "@/lib/legal/documents";

export const Route = createFileRoute("/intake/$token")({
  head: () => ({ meta: [{ title: "Datos del pedido — Truck Company Center" }, { name: "robots", content: "noindex" }] }),
  component: Intake,
});

type FieldValue = string | string[];
type FieldMap = Record<string, FieldValue>;

function labelForIdentity(k: string): string {
  switch (k) {
    case "legal_full_name": return "Nombre legal completo";
    case "email": return "Email";
    case "phone": return "Teléfono";
    default: return k;
  }
}

// id helpers used for scrollIntoView/focus
function fieldDomId(scope: "identity" | "shared" | "service", key: string, slug?: string) {
  return `intake-field-${scope}${slug ? "-" + slug : ""}-${key}`;
}

function Intake() {
  const { token } = Route.useParams();
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [policiesAck, setPoliciesAck] = useState(false);
  const [authorizationAck, setAuthorizationAck] = useState(false);
  const consent = policiesAck && authorizationAck;
  const ack = policiesAck && authorizationAck;
  const [loading, setLoading] = useState(true);
  const [serviceSlugs, setServiceSlugs] = useState<string[]>([]);
  const [detectionFailed, setDetectionFailed] = useState(false);
  const [paid, setPaid] = useState(false);
  const [orderStatus, setOrderStatus] = useState<string>("");
  const [orderShort, setOrderShort] = useState<string>("");
  const [orderItems, setOrderItems] = useState<Array<{ name: string; price: number; type: string }>>([]);
  const [alreadySubmittedAt, setAlreadySubmittedAt] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);

  const [identity, setIdentity] = useState<{
    legal_full_name: string;
    email: string;
    phone: string;
  }>({ legal_full_name: "", email: "", phone: "" });
  const [notes, setNotes] = useState("");
  const [shared, setShared] = useState<FieldMap>({});
  const [services, setServices] = useState<Record<string, FieldMap>>({});

  // UI flags (drive conditional logic + required fields)
  const [useNewGmailAsBusinessEmail, setUseNewGmailAsBusinessEmail] = useState(false);
  const [useCompanyEmailForPlatforms, setUseCompanyEmailForPlatforms] = useState(false);

  const [legal, setLegal] = useState({
    company_name: "",
    signer_title: "owner" as "owner" | "authorized_representative" | "other",
    typed_signature: "",
  });
  const updateLegal = <K extends keyof typeof legal>(k: K, v: (typeof legal)[K]) =>
    setLegal((p) => ({ ...p, [k]: v }));

  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    (async () => {
      try {
        const { data, error } = await (supabase as any).rpc("get_order_for_intake", { p_token: token });
        if (error) throw error;
        const row = Array.isArray(data) ? data[0] : data;
        const slugs: string[] = Array.isArray(row?.service_slugs) ? row.service_slugs : [];
        const filtered = slugs.filter((s) => SERVICE_REQUIREMENTS[s]);
        setServiceSlugs(filtered);
        // Default LLC nueva: si pidió LLC, asume nueva por defecto y oculta legal_company_name
        if (filtered.includes("llc-florida")) {
          setServices((p) => ({ ...p, "llc-florida": { ...(p["llc-florida"] ?? {}), existing_company: "no" } }));
          setShared((p) => ({ ...p, state_of_formation: "Florida" }));
        }
        setPaid(!!row?.paid);
        setOrderStatus(row?.status ?? "");
        setOrderShort(row?.order_short ?? "");
        setOrderItems(Array.isArray(row?.items) ? row.items : []);
        setAlreadySubmittedAt(row?.intake_submitted_at ?? null);
        trackIntakeOpened();
      } catch (err) {
        console.error("get_order_for_intake error:", err);
        setDetectionFailed(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const llcExisting = typeof services["llc-florida"]?.existing_company === "string"
    ? (services["llc-florida"]?.existing_company as string)
    : "";

  const uiFlags: UIFlags = useMemo(() => ({
    use_new_company_gmail_for_business_email: useNewGmailAsBusinessEmail && serviceSlugs.includes("gmail-compania"),
    use_company_email_for_platforms: useCompanyEmailForPlatforms &&
      (serviceSlugs.includes("central-dispatch") || serviceSlugs.includes("super-dispatch")),
    llc_existing_company: llcExisting,
  }), [useNewGmailAsBusinessEmail, useCompanyEmailForPlatforms, llcExisting, serviceSlugs]);

  const conditional = useMemo(() => applyConditionalRules(serviceSlugs, uiFlags), [serviceSlugs, uiFlags]);

  const allRequiredShared = useMemo(() => {
    const set = new Set<string>();
    for (const slug of serviceSlugs) {
      const def = SERVICE_REQUIREMENTS[slug];
      if (!def) continue;
      for (const k of def.sharedFields) {
        if (!conditional.hiddenSharedKeys.has(k)) set.add(k);
      }
    }
    return set;
  }, [serviceSlugs, conditional]);

  const groupsToRender = useMemo(() => {
    return RENDER_GROUPS.map((g) => {
      const sharedKeys = g.sharedKeys.filter((k) => allRequiredShared.has(k));
      const slugs = g.serviceSlugs.filter((s) => serviceSlugs.includes(s));
      return { ...g, sharedKeys, slugs };
    }).filter((g) => g.sharedKeys.length > 0 || g.slugs.length > 0);
  }, [allRequiredShared, serviceSlugs]);

  const updateIdentity = <K extends keyof typeof identity>(k: K, v: (typeof identity)[K]) =>
    setIdentity((p) => ({ ...p, [k]: v }));
  const updateShared = (k: string, v: FieldValue) => setShared((p) => ({ ...p, [k]: v }));
  const updateService = (slug: string, k: string, v: FieldValue) =>
    setServices((p) => ({ ...p, [slug]: { ...(p[slug] ?? {}), [k]: v } }));

  const legalComplete = policiesAck && authorizationAck && legal.typed_signature.trim().length > 1;

  const required = useMemo(() => computeRequiredFields(serviceSlugs, uiFlags), [serviceSlugs, uiFlags]);

  const requiredKeySet = useMemo(() => {
    const m = { identity: new Set<string>(), shared: new Set<string>(), services: {} as Record<string, Set<string>> };
    required.identity.forEach((k) => m.identity.add(k));
    required.shared.forEach((k) => {
      if (!conditional.hiddenSharedKeys.has(k)) m.shared.add(k);
    });
    for (const [slug, keys] of Object.entries(required.services)) {
      const hidden = conditional.hiddenFieldsBySlug[slug] ?? new Set<string>();
      m.services[slug] = new Set(keys.filter((k) => !hidden.has(k)));
    }
    // Cross-validations LLC
    if (serviceSlugs.includes("llc-florida")) {
      if (llcExisting === "yes") {
        // current_entity_name (or legal_company_name) required
        if (!m.services["llc-florida"]) m.services["llc-florida"] = new Set();
        m.services["llc-florida"].add("current_entity_name");
      } else if (llcExisting === "no") {
        m.shared.add("desired_company_names");
      }
    }
    return m;
  }, [required, conditional, serviceSlugs, llcExisting]);

  const missingRequired = useMemo(() => {
    const missing: { scope: "identity" | "shared" | "service"; key: string; slug?: string; label: string }[] = [];
    for (const k of requiredKeySet.identity) {
      const v = (identity as Record<string, string>)[k] ?? "";
      if (isEmptyValue(v)) missing.push({ scope: "identity", key: k, label: labelForIdentity(k) });
    }
    for (const k of requiredKeySet.shared) {
      if (isEmptyValue(shared[k])) missing.push({ scope: "shared", key: k, label: SHARED_FIELDS[k]?.label ?? k });
    }
    for (const [slug, keys] of Object.entries(requiredKeySet.services)) {
      for (const k of keys) {
        // Allow current_entity_name to be satisfied by shared.legal_company_name
        if (slug === "llc-florida" && k === "current_entity_name" && !isEmptyValue(shared["legal_company_name"])) continue;
        if (isEmptyValue(services[slug]?.[k])) {
          const def = SERVICE_REQUIREMENTS[slug]?.fields.find((f) => f.key === k);
          missing.push({ scope: "service", key: k, slug, label: def?.label ?? k });
        }
      }
    }
    return missing;
  }, [requiredKeySet, identity, shared, services]);

  // Helpers for visual error marking
  const missingKeys = useMemo(() => {
    const s = new Set<string>();
    missingRequired.forEach((m) => s.add(`${m.scope}|${m.slug ?? ""}|${m.key}`));
    return s;
  }, [missingRequired]);
  const isMissing = (scope: "identity" | "shared" | "service", key: string, slug?: string) =>
    showErrors && missingKeys.has(`${scope}|${slug ?? ""}|${key}`);

  const requiredMark = (scope: "identity" | "shared" | "service", key: string, slug?: string) => {
    if (scope === "identity" && requiredKeySet.identity.has(key)) return true;
    if (scope === "shared" && requiredKeySet.shared.has(key)) return true;
    if (scope === "service" && slug && requiredKeySet.services[slug]?.has(key)) return true;
    return false;
  };

  function cleanArray(arr: string[]): string[] {
    return arr.map((s) => s.trim()).filter((s) => s.length > 0);
  }
  function valueForPayload(v: FieldValue): string {
    return Array.isArray(v) ? cleanArray(v).join(" | ") : v;
  }
  function valueForSections(v: FieldValue): FieldValue {
    if (Array.isArray(v)) {
      const c = cleanArray(v);
      return c.length <= 1 ? c[0] ?? "" : c;
    }
    return v;
  }

  function scrollToFirstMissing() {
    const first = missingRequired[0];
    if (!first) return;
    const id = fieldDomId(first.scope, first.key, first.slug);
    const el = document.getElementById(id) ?? formRef.current?.querySelector<HTMLElement>(`[data-intake-key="${id}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      const focusable = el.querySelector<HTMLElement>("input, textarea, select, button");
      (focusable ?? el).focus?.();
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setShowErrors(true);
    if (missingRequired.length > 0) {
      toast.error(`Faltan ${missingRequired.length} campos obligatorios. Revisa los marcados en rojo.`);
      setTimeout(scrollToFirstMissing, 50);
      return;
    }
    if (!consent || !ack) {
      toast.error("Debes aceptar los consentimientos legales");
      return;
    }
    if (!legalComplete) {
      toast.error("Debes completar y firmar la sección de acuerdos legales");
      return;
    }
    setSubmitting(true);
    try {
      // Si el cliente eligió usar el Gmail nuevo como business_email, marcamos un valor placeholder server-side.
      const effectiveShared: FieldMap = { ...shared };
      if (uiFlags.use_new_company_gmail_for_business_email) {
        effectiveShared["business_email"] = "pending_new_company_gmail";
      }

      const effectiveServices: Record<string, FieldMap> = { ...services };
      // Si NO usa email de compañía para plataformas, propagar dispatch_account_email a ambas plataformas.
      if (!uiFlags.use_company_email_for_platforms && shared["dispatch_account_email"]) {
        const email = String(shared["dispatch_account_email"]);
        if (serviceSlugs.includes("central-dispatch")) {
          effectiveServices["central-dispatch"] = { ...(effectiveServices["central-dispatch"] ?? {}), central_account_email: email };
        }
        if (serviceSlugs.includes("super-dispatch")) {
          effectiveServices["super-dispatch"] = { ...(effectiveServices["super-dispatch"] ?? {}), super_account_email: email };
        }
      }

      const payload: Record<string, any> = {
        legal_full_name: identity.legal_full_name,
        email: identity.email,
        phone: identity.phone,
        notes,
      };
      for (const [k, v] of Object.entries(effectiveShared)) payload[k] = valueForPayload(v);
      for (const slug of serviceSlugs) {
        const data = effectiveServices[slug] ?? {};
        for (const [k, v] of Object.entries(data)) payload[`${slug}__${k}`] = valueForPayload(v);
      }

      const cleanedShared: FieldMap = {};
      for (const [k, v] of Object.entries(effectiveShared)) cleanedShared[k] = valueForSections(v);
      const cleanedServices: Record<string, FieldMap> = {};
      for (const slug of serviceSlugs) {
        const data = effectiveServices[slug] ?? {};
        const out: FieldMap = {};
        for (const [k, v] of Object.entries(data)) out[k] = valueForSections(v);
        cleanedServices[slug] = out;
      }

      const sections = {
        identity: {
          legal_full_name: identity.legal_full_name,
          email: identity.email,
          phone: identity.phone,
        },
        shared: cleanedShared,
        services: cleanedServices,
        notes,
        detected_service_slugs: serviceSlugs,
        ui_flags: {
          use_new_company_gmail_for_business_email: !!uiFlags.use_new_company_gmail_for_business_email,
          use_company_email_for_platforms: !!uiFlags.use_company_email_for_platforms,
          llc_existing_company: llcExisting,
        },
      };

      // Para LLC nueva, no enviamos company_name en la sección legal
      const llcIsNew = serviceSlugs.includes("llc-florida") && llcExisting === "no";
      const legalCompanyName = llcIsNew ? "" : legal.company_name;

      const submitRes = await fetch("/api/public/submit-intake", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          payload,
          sections,
          consent,
          legal: {
            legal_full_name: identity.legal_full_name,
            company_name: legalCompanyName,
            signer_title: legal.signer_title,
            typed_signature: legal.typed_signature,
            accepted_refund_policy: policiesAck,
            accepted_service_agreement: authorizationAck,
            accepted_limited_authorization: authorizationAck,
            accepted_electronic_signature_consent: authorizationAck,
            accepted_information_certification: authorizationAck,
            accepted_privacy_data_consent: authorizationAck,
          },
        }),
      });
      const submitJson = await submitRes.json().catch(() => ({}));
      if (!submitRes.ok || submitJson?.ok === false) {
        throw new Error(submitJson?.error || "Error al enviar el formulario");
      }

      try {
        await fetch("/api/public/send-intake-notification", {
          method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token }),
        });
      } catch (notifyErr) { console.error("notification trigger failed:", notifyErr); }

      try {
        await fetch("/api/public/send-legal-acceptance", {
          method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token }),
        });
      } catch (notifyErr) { console.error("legal acceptance email trigger failed:", notifyErr); }

      setDone(true);
      trackIntakeSubmitted();
      toast.success("Información enviada. Empezamos pronto.");
    } catch (err: any) {
      toast.error(err.message ?? "Error al enviar");
    } finally {
      setSubmitting(false);
    }
  }

  if (done) return (
    <div className="mx-auto max-w-2xl px-4 py-20 text-center">
      <CheckCircle2 className="mx-auto h-12 w-12 text-success" />
      <h1 className="mt-4 font-display text-3xl font-semibold text-foreground">¡Recibido!</h1>
      <p className="mt-2 text-muted-foreground">Empezamos con el proceso de tu pedido. Te contactaremos pronto.</p>
    </div>
  );

  if (loading) return (
    <div className="mx-auto max-w-3xl px-4 py-20 text-center text-muted-foreground">Cargando…</div>
  );

  if (alreadySubmittedAt) {
    const dateFmt = new Date(alreadySubmittedAt).toLocaleString("es-US", { dateStyle: "long", timeStyle: "short" });
    return (
      <div className="mx-auto max-w-2xl px-4 py-20 text-center">
        <CheckCircle2 className="mx-auto h-12 w-12 text-success" />
        <h1 className="mt-4 font-display text-3xl font-semibold text-foreground">Formulario ya enviado</h1>
        <p className="mt-2 text-muted-foreground">Este formulario ya fue enviado el {dateFmt}. Si necesitas corregir algo, contáctanos por WhatsApp.</p>
        <a href="https://wa.me/17867214284?text=Hola%2C%20soy%20cliente%20de%20Truck%20Company%20Center.%20Necesito%20ayuda%20con%20mi%20formulario%20o%20pedido." target="_blank" rel="noreferrer"
          className="mt-6 inline-block rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90">
          Contactar por WhatsApp
        </a>
      </div>
    );
  }

  const showFallback = detectionFailed || serviceSlugs.length === 0;

  const statusLabel: Record<string, string> = {
    nuevo: "Pago pendiente",
    pagado: "Pagado",
    intake_pendiente: "Formulario pendiente",
    en_proceso: "En proceso",
    esperando_cliente: "Esperando cliente",
    completado: "Completado",
    cancelado: "Cancelado",
  };

  // Dynamic operating_states label based on intrastate/interstate
  const interstateChoice = typeof shared["interstate_or_intrastate"] === "string" ? (shared["interstate_or_intrastate"] as string) : "";
  const llcFloridaIncluded = serviceSlugs.includes("llc-florida");
  const dynamicSharedDef = (def: FieldDef): FieldDef => {
    if (def.key === "state_of_formation" && llcFloridaIncluded) {
      return {
        ...def,
        label: "Estado donde se formará la compañía",
        help: "Este servicio aplica para LLC en Florida.",
        placeholder: "Florida",
      };
    }
    if (def.key === "operating_states") {
      if (interstateChoice === "interstate") {
        return {
          ...def,
          label: "Rutas o regiones principales donde planea operar",
          help: "Ej.: Florida–Texas, Florida–Georgia, Midwest, East Coast, todo Estados Unidos",
          placeholder: "Ej.: Florida–Texas",
        };
      }
      return {
        ...def,
        label: "Estado donde va a operar",
        help: "Ej.: Florida",
        placeholder: "Florida",
      };
    }
    return def;
  };

  return (
    <form ref={formRef} onSubmit={submit} noValidate className="mx-auto max-w-3xl px-4 py-12 md:px-6">
      <h1 className="font-display text-3xl font-semibold text-foreground md:text-4xl">Formulario de datos del pedido</h1>
      <p className="mt-2 text-muted-foreground">Completa solo la información necesaria para los servicios que compraste.</p>

      {orderShort && (
        <div className="mt-4 rounded-xl border border-hairline bg-surface p-4 text-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="font-medium text-foreground">Pedido #{orderShort}</div>
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${paid ? "bg-success/15 text-[oklch(0.78_0.16_155)]" : "bg-yellow-500/15 text-yellow-300"}`}>
              {statusLabel[orderStatus] ?? orderStatus}
            </span>
          </div>
          {orderItems.length > 0 && (
            <ul className="mt-2 text-xs text-muted-foreground">
              {orderItems.map((it, i) => (<li key={i} className="truncate">• {it.name}</li>))}
            </ul>
          )}
        </div>
      )}

      {!paid && (
        <div className="mt-4 rounded-md border border-yellow-500/40 bg-yellow-500/10 p-3 text-sm text-foreground">
          Este formulario estará disponible cuando el pago esté confirmado.
        </div>
      )}

      {showFallback && (
        <div className="mt-4 rounded-md border border-hairline bg-surface p-3 text-xs text-muted-foreground">
          Mostrando un formulario base. Si te falta una sección, déjanoslo saber en las notas y te escribimos.
        </div>
      )}

      {/* Identidad */}
      <Section title="Identidad">
        {(["legal_full_name", "email", "phone"] as const).map((k) => {
          const id = fieldDomId("identity", k);
          const miss = isMissing("identity", k);
          const req = requiredMark("identity", k);
          const inputType = k === "email" ? "email" : k === "phone" ? "tel" : "text";
          return (
            <div key={k} className="space-y-1.5" id={id}>
              <Label htmlFor={`${id}-input`} className="text-xs text-muted-foreground">
                {labelForIdentity(k)}{req && <RequiredStar />}
              </Label>
              <Input id={`${id}-input`} type={inputType}
                aria-invalid={miss || undefined}
                className={`bg-surface border-hairline ${miss ? "border-destructive ring-1 ring-destructive" : ""}`}
                value={identity[k]}
                onChange={(e) => updateIdentity(k, e.target.value)}
              />
              {miss && <ErrorText />}
            </div>
          );
        })}
      </Section>

      {/* Grupos lógicos */}
      {groupsToRender.map((g) => {
        const truckDefinedVal = g.id === "operation" && typeof shared["truck_defined"] === "string" ? (shared["truck_defined"] as string) : "";
        const showTruckPendingNote = g.id === "operation" && (truckDefinedVal === "no" || truckDefinedVal === "en_proceso");
        // Reorder: in operation group, render interstate_or_intrastate before operating_states
        let sharedKeys = g.sharedKeys;
        if (g.id === "operation") {
          const ordered = ["interstate_or_intrastate", ...sharedKeys.filter((k) => k !== "interstate_or_intrastate")];
          sharedKeys = ordered.filter((k) => sharedKeys.includes(k));
        }
        // Reorder company group: when LLC nueva, desired first; existente, legal_company_name first
        if (g.id === "company") {
          const llcNew = serviceSlugs.includes("llc-florida") && llcExisting === "no";
          const front = llcNew ? ["desired_company_names"] : ["legal_company_name"];
          const rest = sharedKeys.filter((k) => !front.includes(k));
          sharedKeys = [...front.filter((k) => sharedKeys.includes(k)), ...rest];
        }

        return (
          <Section key={g.id} title={g.title}>
            {showTruckPendingNote ? (
              <div className="md:col-span-2 rounded-md border border-hairline bg-surface-2 p-3 text-xs text-muted-foreground">
                Podemos avanzar con la compañía y dejar USDOT/MC pendiente hasta confirmar los datos reales de la operación.
              </div>
            ) : null}

            {sharedKeys.map((k) => {
              const baseDef = SHARED_FIELDS[k];
              if (!baseDef) return null;
              const def = dynamicSharedDef(baseDef);
              const id = fieldDomId("shared", k);
              const miss = isMissing("shared", k);
              const req = requiredMark("shared", k);
              const lockedFlorida = k === "state_of_formation" && llcFloridaIncluded;
              return (
                <FieldRenderer
                  key={k}
                  domId={id}
                  def={def}
                  required={req}
                  missing={miss}
                  readOnly={lockedFlorida}
                  value={lockedFlorida ? "Florida" : (shared[k] ?? (def.repeatable ? [""] : ""))}
                  onChange={(v) => { if (!lockedFlorida) updateShared(k, v); }}
                />
              );
            })}

            {/* Toggle: usar Gmail nuevo como email de compañía */}
            {g.id === "company" && serviceSlugs.includes("gmail-compania") && useNewGmailAsBusinessEmail && (
              <div className="md:col-span-2 rounded-md border border-hairline bg-surface-2 p-3 text-xs text-muted-foreground">
                Email de compañía pendiente: usaremos el Gmail que Truck Company Center creará para ti.
              </div>
            )}
            {g.id === "company" && serviceSlugs.includes("gmail-compania") && (
              <label className="md:col-span-2 flex items-start gap-3 text-sm text-foreground">
                <Checkbox checked={useNewGmailAsBusinessEmail} onCheckedChange={(v) => setUseNewGmailAsBusinessEmail(!!v)} />
                <span>Usar el Gmail de compañía que Truck Company Center creará para mí como email de la compañía.</span>
              </label>
            )}

            {/* Toggle: usar email de compañía/Gmail TCC para plataformas */}
            {g.id === "platforms" && (serviceSlugs.includes("central-dispatch") || serviceSlugs.includes("super-dispatch")) && (
              <label className="md:col-span-2 flex items-start gap-3 text-sm text-foreground">
                <Checkbox checked={useCompanyEmailForPlatforms} onCheckedChange={(v) => setUseCompanyEmailForPlatforms(!!v)} />
                <span>{serviceSlugs.includes("gmail-compania")
                  ? "Usar el Gmail de compañía que Truck Company Center creará para las cuentas de plataformas."
                  : "Usar el email de compañía indicado arriba para las cuentas de plataformas."}</span>
              </label>
            )}

            {g.slugs.map((slug) => {
              const def = SERVICE_REQUIREMENTS[slug];
              if (!def) return null;
              const hidden = conditional.hiddenFieldsBySlug[slug] ?? new Set<string>();
              let visibleFields = def.fields.filter((f) => !hidden.has(f.key));
              if (slug === "llc-florida" && services["llc-florida"]?.existing_company !== "yes") {
                visibleFields = visibleFields.filter((f) => f.key !== "current_entity_name");
              }
              const smartPlaceholder = (key: string): string | undefined => {
                if (slug === "tarjetas-negocio") {
                  if (key === "card_name" && identity.legal_full_name) return `Sugerencia: ${identity.legal_full_name}`;
                  if (key === "card_phone" && shared["business_phone"]) return `Sugerencia: ${shared["business_phone"]}`;
                  if (key === "card_email" && shared["business_email"]) return `Sugerencia: ${shared["business_email"]}`;
                  if (key === "card_address" && shared["business_address"]) return `Sugerencia: ${shared["business_address"]}`;
                }
                if (slug === "logo" && key === "logo_text") {
                  if (shared["legal_company_name"]) return `Sugerencia: ${shared["legal_company_name"]}`;
                  const desired = shared["desired_company_names"];
                  if (Array.isArray(desired) && desired[0]) return `Sugerencia: ${desired[0]}`;
                }
                return undefined;
              };
              const notices = conditional.noticesBySlug[slug] ?? [];
              const notice = def.notice;
              const hasContent = visibleFields.length > 0 || notices.length > 0 || notice;
              if (!hasContent) return null;
              return (
                <div key={slug} className="md:col-span-2">
                  <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    {def.title}
                  </div>
                  {notice && (
                    <div className="mb-3 rounded-md border border-hairline bg-surface-2 p-3 text-xs text-muted-foreground">{notice}</div>
                  )}
                  {notices.map((n, i) => (
                    <div key={i} className="mb-3 rounded-md border border-hairline bg-surface-2 p-3 text-xs text-muted-foreground">{n}</div>
                  ))}
                  <div className="grid gap-4 md:grid-cols-2">
                    {visibleFields.map((field) => {
                      const ph = smartPlaceholder(field.key);
                      const fieldDef = ph ? { ...field, placeholder: field.placeholder ?? ph } : field;
                      const id = fieldDomId("service", field.key, slug);
                      const miss = isMissing("service", field.key, slug);
                      const req = requiredMark("service", field.key, slug);
                      return (
                        <FieldRenderer
                          key={field.key}
                          domId={id}
                          def={fieldDef}
                          required={req}
                          missing={miss}
                          value={services[slug]?.[field.key] ?? (field.repeatable ? [""] : "")}
                          onChange={(v) => updateService(slug, field.key, v)}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </Section>
        );
      })}

      {/* Notas */}
      <div className="mt-6 rounded-xl border border-hairline bg-surface p-6">
        <Label className="text-xs text-muted-foreground">Notas adicionales</Label>
        <Textarea rows={4} className="mt-2 bg-surface-2 border-hairline" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>

      {/* Consentimientos y firma */}
      <div className="mt-6 rounded-xl border border-primary/40 bg-surface p-6">
        <div className="text-xs uppercase tracking-[0.2em] text-label-accent">Consentimientos y firma</div>
        <p className="mt-2 text-sm text-muted-foreground">
          Para poder procesar tu pedido, necesitamos que aceptes las políticas y nos autorices a trabajar administrativamente con la información que nos proporcionaste.
        </p>

        <div className="mt-4 space-y-2">
          <details className="rounded-md border border-hairline bg-surface-2 p-3">
            <summary className="cursor-pointer text-sm font-medium text-foreground">Ver Service Agreement (v{LEGAL_VERSIONS.service_agreement})</summary>
            <div className="mt-3 max-h-72 overflow-y-auto pr-2"><ServiceAgreementBody /></div>
          </details>
          <details className="rounded-md border border-hairline bg-surface-2 p-3">
            <summary className="cursor-pointer text-sm font-medium text-foreground">Ver Limited Authorization (v{LEGAL_VERSIONS.limited_authorization})</summary>
            <div className="mt-3 max-h-72 overflow-y-auto pr-2"><LimitedAuthorizationBody /></div>
          </details>
          <details className="rounded-md border border-hairline bg-surface-2 p-3">
            <summary className="cursor-pointer text-sm font-medium text-foreground">Ver Electronic Signature Consent (v{LEGAL_VERSIONS.electronic_signature_consent})</summary>
            <div className="mt-3 max-h-72 overflow-y-auto pr-2"><ElectronicSignatureConsentBody /></div>
          </details>
          <details className="rounded-md border border-hairline bg-surface-2 p-3">
            <summary className="cursor-pointer text-sm font-medium text-foreground">Ver Privacy / Data Consent (v{LEGAL_VERSIONS.privacy_policy})</summary>
            <div className="mt-3 max-h-72 overflow-y-auto pr-2"><PrivacyDataConsentBody /></div>
          </details>
          <details className="rounded-md border border-hairline bg-surface-2 p-3">
            <summary className="cursor-pointer text-sm font-medium text-foreground">Ver Information Certification</summary>
            <div className="mt-3 max-h-72 overflow-y-auto pr-2"><InformationCertificationBody /></div>
          </details>
        </div>

        <div className="mt-5 grid gap-4 md:grid-cols-2">
          {/* Company name only when NOT new LLC */}
          {!(serviceSlugs.includes("llc-florida") && llcExisting === "no") && (
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Company name (if applicable)</Label>
              <Input className="bg-surface-2 border-hairline" value={legal.company_name}
                onChange={(e) => updateLegal("company_name", e.target.value)} />
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Your relationship to the company<RequiredStar /></Label>
            <div className="flex gap-2">
              {([["owner","Owner"],["authorized_representative","Authorized Rep."],["other","Other"]] as const).map(([v,l]) => (
                <button type="button" key={v} onClick={() => updateLegal("signer_title", v)}
                  className={`flex-1 rounded-md border px-3 py-2 text-xs ${legal.signer_title===v ? "border-primary bg-primary/10 text-foreground" : "border-hairline bg-surface text-muted-foreground"}`}>{l}</button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label className="text-xs text-muted-foreground">Firma electrónica (escribe tu nombre legal completo)<RequiredStar /></Label>
            <Input className={`bg-surface-2 border-hairline font-display text-lg italic ${showErrors && legal.typed_signature.trim().length <= 1 ? "border-destructive ring-1 ring-destructive" : ""}`}
              value={legal.typed_signature}
              placeholder="Escribe tu nombre legal completo"
              onChange={(e) => updateLegal("typed_signature", e.target.value)} />
            <p className="text-[11px] text-muted-foreground">Al escribir tu nombre, aceptas que esto constituye tu firma electrónica para este pedido.</p>
          </div>
        </div>

        <div className="mt-5 space-y-3">
          <label className="flex items-start gap-3 text-sm text-foreground">
            <Checkbox checked={policiesAck} onCheckedChange={(v) => setPoliciesAck(!!v)} />
            <span>Acepto la <strong>Política de Privacidad</strong> y la <strong>Política de Reembolso</strong> de Truck Company Center.<RequiredStar /></span>
          </label>
          <label className="flex items-start gap-3 text-sm text-foreground">
            <Checkbox checked={authorizationAck} onCheckedChange={(v) => setAuthorizationAck(!!v)} />
            <span>Acepto el <strong>Service Agreement</strong>, la <strong>Limited Authorization</strong>, el consentimiento de firma electrónica y certifico que la información enviada es correcta. Autorizo a Truck Company Center a usar la información proporcionada para asistir administrativamente con los servicios comprados.<RequiredStar /></span>
          </label>
        </div>
      </div>

      <div className="mt-6"><Disclaimer /></div>

      {showErrors && missingRequired.length > 0 && (
        <div className="mt-6 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-foreground" role="alert">
          Faltan {missingRequired.length} campos obligatorios. Revisa los campos marcados en rojo.
        </div>
      )}

      <Button type="submit" size="lg"
        className="mt-6 w-full bg-primary text-primary-foreground hover:bg-primary/90"
        disabled={submitting || !paid || !!alreadySubmittedAt}>
        {submitting ? "Enviando..." : "Enviar información del pedido"}
      </Button>
    </form>
  );
}

function RequiredStar() {
  return <span className="text-red-500" aria-hidden="true"> *</span>;
}
function ErrorText({ msg }: { msg?: string }) {
  return <p className="text-[11px] font-medium text-destructive">{msg ?? "Este campo es obligatorio."}</p>;
}

function FieldRenderer({
  def, value, onChange, domId, required, missing, readOnly,
}: {
  def: FieldDef;
  value: FieldValue;
  onChange: (v: FieldValue) => void;
  domId: string;
  required?: boolean;
  missing?: boolean;
  readOnly?: boolean;
}) {
  if (def.repeatable) {
    const arr = Array.isArray(value) ? value : (value ? [value] : [""]);
    return (
      <div className="md:col-span-2" id={domId}>
        <RepeatableInput
          label={def.label}
          required={required}
          missing={missing}
          addLabel={def.addLabel ?? "Agregar otro"}
          values={arr}
          onChange={onChange}
          inputType={def.type === "tel" || def.type === "email" ? def.type : "text"}
          placeholder={def.placeholder}
          privacyWarning={def.privacyWarning}
          help={def.help}
        />
      </div>
    );
  }

  const v = typeof value === "string" ? value : Array.isArray(value) ? value.join(" ") : "";
  const errCls = missing ? "border-destructive ring-1 ring-destructive" : "";

  if (def.type === "textarea") {
    return (
      <div className="space-y-1.5 md:col-span-2" id={domId}>
        <Label className="text-xs text-muted-foreground">{def.label}{required && <RequiredStar />}</Label>
        <Textarea rows={3} aria-invalid={missing || undefined}
          className={`bg-surface-2 border-hairline ${errCls}`} value={v}
          placeholder={def.placeholder} onChange={(e) => onChange(e.target.value)} />
        {def.help && <p className="text-[11px] text-muted-foreground">{def.help}</p>}
        {def.privacyWarning && <p className="text-[11px] text-muted-foreground">{def.privacyWarning}</p>}
        {missing && <ErrorText />}
      </div>
    );
  }
  if (def.type === "yesno") {
    return (
      <div className="space-y-1.5" id={domId}>
        <Label className="text-xs text-muted-foreground">{def.label}{required && <RequiredStar />}</Label>
        <div className={`flex gap-2 rounded-md ${missing ? "ring-1 ring-destructive" : ""}`}>
          {([["yes", "Sí"], ["no", "No"]] as [string, string][]).map(([val, l]) => (
            <button type="button" key={val} onClick={() => onChange(val)}
              className={`flex-1 rounded-md border px-3 py-2 text-sm ${v === val ? "border-primary bg-primary/10 text-foreground" : "border-hairline bg-surface text-muted-foreground"}`}>
              {l}
            </button>
          ))}
        </div>
        {def.help && <p className="text-[11px] text-muted-foreground">{def.help}</p>}
        {missing && <ErrorText />}
      </div>
    );
  }
  if (def.type === "select" && def.options) {
    return (
      <div className="space-y-1.5" id={domId}>
        <Label className="text-xs text-muted-foreground">{def.label}{required && <RequiredStar />}</Label>
        <div className={`flex flex-wrap gap-2 rounded-md ${missing ? "ring-1 ring-destructive p-1" : ""}`}>
          {def.options.map(([val, l]) => (
            <button type="button" key={val} onClick={() => onChange(val)}
              className={`flex-1 min-w-[120px] rounded-md border px-3 py-2 text-sm ${v === val ? "border-primary bg-primary/10 text-foreground" : "border-hairline bg-surface text-muted-foreground"}`}>
              {l}
            </button>
          ))}
        </div>
        {def.help && <p className="text-[11px] text-muted-foreground">{def.help}</p>}
        {missing && <ErrorText />}
      </div>
    );
  }
  return (
    <div className="space-y-1.5" id={domId}>
      <Label className="text-xs text-muted-foreground">{def.label}{required && <RequiredStar />}</Label>
      <Input type={def.type ?? "text"} aria-invalid={missing || undefined}
        readOnly={readOnly} disabled={readOnly}
        className={`bg-surface border-hairline ${errCls} ${readOnly ? "opacity-80 cursor-not-allowed" : ""}`} value={v}
        placeholder={def.placeholder} onChange={(e) => { if (!readOnly) onChange(e.target.value); }} />
      {def.help && <p className="text-[11px] text-muted-foreground">{def.help}</p>}
      {def.privacyWarning && <p className="text-[11px] text-muted-foreground">{def.privacyWarning}</p>}
      {missing && <ErrorText />}
    </div>
  );
}

function RepeatableInput({
  label, addLabel, values, onChange, inputType = "text", placeholder, privacyWarning, help, required, missing,
}: {
  label: string; addLabel: string; values: string[]; onChange: (v: string[]) => void;
  inputType?: string; placeholder?: string; privacyWarning?: string; help?: string;
  required?: boolean; missing?: boolean;
}) {
  const safe = values.length === 0 ? [""] : values;
  const update = (idx: number, v: string) => { const next = [...safe]; next[idx] = v; onChange(next); };
  const add = () => onChange([...safe, ""]);
  const remove = (idx: number) => {
    if (idx === 0) return;
    const next = safe.filter((_, i) => i !== idx);
    onChange(next.length === 0 ? [""] : next);
  };
  const errCls = missing ? "border-destructive ring-1 ring-destructive" : "";
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}{required && <RequiredStar />}</Label>
      <div className="space-y-2">
        {safe.map((val, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <Input type={inputType} className={`bg-surface border-hairline ${idx === 0 ? errCls : ""}`} value={val}
              aria-invalid={missing && idx === 0 ? true : undefined}
              placeholder={placeholder} onChange={(e) => update(idx, e.target.value)} />
            {idx > 0 && (
              <button type="button" onClick={() => remove(idx)} aria-label="Quitar"
                className="rounded-md border border-hairline bg-surface p-2 text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
      </div>
      <button type="button" onClick={add} className="mt-1 inline-flex items-center gap-1 text-xs text-primary hover:underline">
        <Plus className="h-3.5 w-3.5" /> {addLabel}
      </button>
      {help && <p className="text-[11px] text-muted-foreground">{help}</p>}
      {privacyWarning && <p className="text-[11px] text-muted-foreground">{privacyWarning}</p>}
      {missing && <ErrorText />}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-6 rounded-xl border border-hairline bg-surface p-6">
      <div className="text-xs uppercase tracking-[0.2em] text-label-accent">{title}</div>
      <div className="mt-4 grid gap-4 md:grid-cols-2">{children}</div>
    </div>
  );
}
